<!-- lib/components/Header.svelte -->
<script>
  import { onMount } from "svelte";
  import { _ } from "svelte-i18n";
  import {
    isReferralPortal,
    APP_URL,
    LEGAL_DOCS_URL,
    GENERIC_DOCS_URL,
  } from "utils/menu.js";
  import LanguageSwitcher from "./LanguageSwitcher.svelte";

  export let showLanguageSwitcher = true;

  let mobileMenuOpen = false;

  // Create reactive button data with translations
  $: buttonData = [
    { labelKey: "header.menu.faq", link: "/docs/most-common" },
    { labelKey: "header.menu.fees", link: "/docs/fees" },
    { labelKey: "header.menu.livePricing", link: "/live-pricing" },
    { labelKey: "header.menu.about", link: "/docs/overview" },
    { labelKey: "header.menu.contact", link: "/contact-us/" },
  ];

  // Add Sign In button if not on referral portal
  $: if (!isReferralPortal()) {
    buttonData = [
      ...buttonData,
      { labelKey: "header.menu.signIn", link: APP_URL },
    ];
  }

  function toggleMobileMenu() {
    mobileMenuOpen = !mobileMenuOpen;
  }

  function closeMobileMenu() {
    mobileMenuOpen = false;
  }

  onMount(() => {
    function handleClickOutside(event) {
      const header = document.querySelector("header");
      if (!header.contains(event.target)) {
        mobileMenuOpen = false;
      }
    }

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  });
</script>

<header>
  <div class="header-content">
    <a href="/" class="logo" aria-label={$_("header.logo.homeLink")}>
      <img
        src="/images/v2/LQN_logo_round.svg"
        alt={$_("header.logo.alt")}
        width="40"
        height="40"
      />
      Liquid Noble
    </a>

    <div class="header-controls">
      <nav class="nav">
        {#each buttonData as menuItem}
          <a href={menuItem.link}>{$_(menuItem.labelKey)}</a>
        {/each}
      </nav>

      {#if showLanguageSwitcher}
        <LanguageSwitcher />
      {/if}

      <button
        class="mobile-menu-toggle"
        class:open={mobileMenuOpen}
        on:click={toggleMobileMenu}
        aria-label={$_("header.mobile.toggleMenu")}
      >
        <span class="hamburger"></span>
        <span class="hamburger"></span>
        <span class="hamburger"></span>
      </button>
    </div>
  </div>

  <div class="mobile-nav" class:open={mobileMenuOpen}>
    {#each buttonData as menuItem}
      <a href={menuItem.link} on:click={closeMobileMenu}
        >{$_(menuItem.labelKey)}</a
      >
    {/each}
    <div class="mobile-language-switcher">
      <LanguageSwitcher />
    </div>
  </div>
</header>

<style lang="scss">
  @keyframes goldShine {
    0% {
      background-position: -200% 0;
    }
    60% {
      background-position: 200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  header {
    background-color: var(--card-bg);
    border-bottom: 1px solid #260d28;
    width: 100%;
    position: relative;
    z-index: 100; // Lower than document overlays
  }

  .header-content {
    max-width: var(--max-width);
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 3rem;
  }

  .logo {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1.5rem;
    font-weight: 700;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.3s ease;
    background: linear-gradient(
      120deg,
      #c6a858 20%,
      #fceabb 40%,
      #dbbb6b 50%,
      #fceabb 60%,
      #c6a858 80%
    );
    background-size: 200% auto;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: goldShine 5s ease-in-out infinite;

    &:hover {
      transform: translateY(-1px);
      filter: brightness(1.1);
    }

    &:active {
      transform: translateY(0);
    }
  }

  .header-controls {
    display: flex;
    align-items: center;
    gap: 2rem;
  }

  .nav {
    display: flex;
    gap: 2.5rem;
  }

  .nav a {
    color: var(--text-color3);
    text-decoration: none;
    font-weight: 500;
    font-size: 1rem;
    transition: all 0.3s ease;
    position: relative;

    &:hover {
      color: #fff;
    }

    &::after {
      content: "";
      position: absolute;
      bottom: -4px;
      left: 0;
      width: 0;
      height: 2px;
      background: var(--text-color1);
      transition: width 0.3s ease;
    }

    &:hover::after {
      width: 100%;
    }
  }

  .mobile-menu-toggle {
    display: none;
    flex-direction: column;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
    gap: 0.25rem;
    z-index: 101; // Slightly higher than header but lower than document overlays
  }

  .hamburger {
    width: 24px;
    height: 3px;
    background-color: var(--text-color3);
    border-radius: 2px;
    transition: all 0.3s ease;
    transform-origin: center;
  }

  .mobile-menu-toggle.open .hamburger:nth-child(1) {
    transform: rotate(45deg) translate(6px, 6px);
  }

  .mobile-menu-toggle.open .hamburger:nth-child(2) {
    opacity: 0;
  }

  .mobile-menu-toggle.open .hamburger:nth-child(3) {
    transform: rotate(-45deg) translate(6px, -6px);
  }

  .mobile-nav {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: var(--card-bg);
    border-bottom: 1px solid #260d28;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    z-index: 500; // Higher than header but lower than document overlays
    opacity: 0;
    transform: translateY(-10px);
    transition: all 0.3s ease;

    &.open {
      display: block;
      opacity: 1;
      transform: translateY(0);
    }
  }

  .mobile-nav a {
    display: block;
    color: var(--text-color3);
    text-decoration: none;
    font-weight: 500;
    font-size: 1.1rem;
    padding: 1.25rem 3rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    transition: all 0.3s ease;

    &:hover {
      background-color: rgba(255, 255, 255, 0.05);
      color: #fff;
      padding-left: 3.5rem;
    }

    &:last-child {
      border-bottom: none;
    }
  }

  .mobile-language-switcher {
    padding: 1.25rem 3rem;
    border-top: 1px solid rgba(255, 255, 255, 0.05);
  }

  @media (max-width: 768px) {
    .nav {
      display: none;
    }

    .mobile-menu-toggle {
      display: flex;
    }

    .header-content {
      padding: 1rem 2rem;
    }

    .header-controls {
      gap: 1rem;
    }
  }

  @media (max-width: 480px) {
    .header-content {
      padding: 1rem 1.5rem;
    }

    .logo {
      font-size: 1.25rem;
    }

    .logo img {
      width: 32px;
      height: 32px;
    }

    .mobile-nav a {
      padding: 1rem 1.5rem;
      font-size: 1rem;

      &:hover {
        padding-left: 2rem;
      }
    }

    .mobile-language-switcher {
      padding: 1rem 1.5rem;
    }
  }

  @media (max-width: 320px) {
    .header-content {
      padding: 0.75rem 1rem;
    }

    .logo {
      font-size: 1.1rem;
      gap: 0.5rem;
    }

    .logo img {
      width: 28px;
      height: 28px;
    }
  }
</style>
